<template>
  <div class="TextInput">
    <div
      v-if="label !== ''"
      class="TextInput-label p p-bold capitalize"
      :class="{
        required: required,
        'app-color-black': labelColor === TEXT_INPUT_COLOR.black,
        'app-color-1': labelColor === TEXT_INPUT_COLOR.purple_dark,
      }"
    >
      {{ label }}
    </div>

    <v-text-field
      v-if="type === 'text'"
      v-model="internalValue"
      :required="required"
      :rules="rules"
      :hint="hint"
      :clearable="clearable"
      :label="label"
      single-line
      :placeholder="internalPlaceholder"
      class=""
      color="#004f9f"
      :prepend-inner-icon="icon ? icon : null"
      outlined
      :hide-details="hideDetails"
      @focus="onFocus"
      @blur="onBlur"
    />
    <v-text-field
      v-else
      v-model="internalValue"
      :required="required"
      :rules="rules"
      :hint="hint"
      :label="label"
      single-line
      :placeholder="internalPlaceholder"
      class=""
      color="#004f9f"
      :prepend-inner-icon="icon ? icon : null"
      outlined
      :type="showPassword ? 'text' : 'password'"
      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
      @click:append="() => (showPassword = !showPassword)"
      @focus="onFocus"
      @blur="onBlur"
    />
  </div>
</template>

<script type="text/javascript">
  import mixinUi from '@/mixins/utils/ui.js'
  import mixinCurrency from '@/mixins/utils/currency.js'
  import mixinPhoneNumber from '@/mixins/utils/phoneNumber.js'
  import mixinStrFormat from '@/mixins/utils/strFormat.js'

  import { NUMERIC_FORMATS, TEXT_INPUT_COLOR } from './TextInput.const'

  export default {
    mixins: [mixinUi, mixinCurrency, mixinPhoneNumber, mixinStrFormat,],
    props: {
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      format: {
        type: String,
        default: '',
      },
      formatTextTemplate: {
        type: String,
        default: '',
      },
      default: {
        validator: (v) => true,
        default: null,
      },
      value: {
        type: [String, Number,],
        default: '',
      },
      icon: {
        type: String,
        default: '',
      },
      labelColor: {
        type: String,
        default: TEXT_INPUT_COLOR.purple_dark,
      },
      hint: {
        type: String,
        default: '',
      },
      type: {
        type: String,
        default: 'text',
      },
      required: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      rules: {
        type: Array,
        default: () => [],
      },
      hideDetails: {
        type: Boolean,
        default: false,
      },
      clearOnFocus: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        TEXT_INPUT_COLOR,
        hasFocus: false,
        showPassword: false,
        editedValue: '',
      }
    },
    computed: {
      internalValue: {
        get() {
          if (!this.hasFocus) {
            return this.getDisplayValue(this.value)
          }
          if (this.clearOnFocus) {
            return this.editedValue
          }

          return this.value
        },
        set(value) {
          const formattedValue = NUMERIC_FORMATS[this.format] ? this.getNumericValue(value) : value
          this.$emit('input', formattedValue)
          this.editedValue = value
        },
      },
      internalPlaceholder() {
        if (this.placeholder) {
          return this.placeholder
        }

        return this.label || '...'
      },
    },
    methods: {
      onFocus() {
        this.hasFocus = true
      },
      onBlur() {
        this.hasFocus = false
      },
      getDisplayValue(value) {
        if (this.format === 'currency') {
          return this.currency(this.value)
        } else if (this.format === 'currency-2') {
          return this.currency(this.value, 2)
        } else if (this.format === 'month') {
          return this.str_format_month(this.value)
        } else if (this.format === 'year') {
          return this.str_format_year(this.value)
        } else if (this.format === 'percentage') {
          return this.str_format_percentage(this.value)
        } else if (this.format === 'percentage-1') {
          return this.str_format_percentage(this.value, 1)
        } else if (this.format === 'percentage-2') {
          return this.str_format_percentage(this.value, 2)
        } else if (this.format === 'phone') {
          return this.phone(this.value)
        } else if (this.format === 'template-text' && this.formatTextTemplate !== '') {
          return this.formatTextTemplate.replace('{!!}', this.value)
        }

        return value
      },
      getNumericValue(value) {
        if (!value) {
          return 0
        }

        return parseFloat(value)
      },
    },
  }
</script>

<style lang="scss">
/* stylelint-disable max-nesting-depth */
.TextInput {
  .TextInput-label {
    margin-left: 8px;
    margin-bottom: 4px;
    &.required::after {
      content: '*';
    }
  }

  .v-input {
    .v-input__control {
      .v-input__slot {
        .v-input__prepend-inner {
          margin-top: 0;
          height: 48px;
          position: relative;
          left: -11px;
          width: 48px;
          margin-right: -11px;
          border: 1.4px solid $color-d5e4f3;
          border-radius: 8px 0 0 8px;
          background-color: $color-f3f9ff;
          text-align: center;
          padding-right: 0;
          padding-top: 12px;

          .v-input__icon {
            i {
              font-size: 24px;
              color: $color-001c4b !important;
            }
          }
        }

        fieldset {
          max-height: 53px;
          border: 1.4px solid $color-d9dfff;
          border-radius: 8px;
        }
        .v-text-field__slot {
          max-height: 48px;
          label {
            left: 5px !important;
            right: auto !important;
            top: auto !important;
            position: absolute;
          }
          input {
            padding-top: 0;
            padding-bottom: 0;
            padding-left: 8px;
            padding-right: 8px;
            color: $color-004f9f;
            font-size: 16px;
            letter-spacing: 0.24px;
            line-height: 24px;
          }
        }
      }
    }
  }
  .v-input.error--text {
    .v-input__control {
      .v-input__slot {
        .v-input__prepend-inner {
          margin-top: 0;
          height: 48px;
          position: relative;
          left: -11px;
          width: 48px;
          margin-right: -11px;
          border: 1.4px solid $color-ff524f;
          border-radius: 8px 0 0 8px;
          background-color: $color-255_82_79_16;
          text-align: center;
          padding-right: 0;
          padding-top: 12px;

          .v-input__icon {
            i {
              font-size: 24px;
              color: $color-ff524f !important;
            }
          }
        }

        fieldset {
          max-height: 53px;
          border: 1.4px solid $color-ff524f;
          border-radius: 8px;
        }
        .v-text-field__slot {
          max-height: 48px;
          label {
            left: 5px !important;
            right: auto !important;
            top: auto !important;
            position: absolute;
          }
          input {
            padding-top: 0;
            padding-bottom: 0;
            padding-left: 8px;
            padding-right: 8px;
            color: $color-ff524f;
            font-size: 16px;
            letter-spacing: 0.24px;
            line-height: 24px;
          }
        }
      }
    }
  }
}
</style>
